import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { PageShell } from '@/components/PageShell'
import { ScrollManager } from '@/components/ScrollManager'
import { UnderConstruction } from '@/components/UnderConstruction'
import { FrontPage } from '@/pages/FrontPage'
import { TarinaPage } from '@/pages/TarinaPage'
import { NotFound } from '@/pages/placeholders'
import { AREAS } from '@/lib/site'
import { t } from '@/lib/ui'

// The walk carries the simulator engine + the ~5 MB compiled vault-index chunk,
// so it is lazy-loaded — the front page never pays for it (DESIGN-BRIEF §6 /
// SIM-014 "the vault is the backend").
const WalkPage = lazy(() => import('@/pages/walk/WalkPage').then((m) => ({ default: m.WalkPage })))

// The data-model page carries the mermaid dependency (large, WP-F7) — lazy for
// the same reason: the front page must never pay for it.
const DataModelPage = lazy(() =>
  import('@/pages/data-model/DataModelPage').then((m) => ({ default: m.DataModelPage })),
)

// The citizen catalog carries benefits.json + benefit-cards.fi.json (WP-F4) —
// real copy weight for 46 cards. Lazy for the same reason as the two above.
const KansalaisellePage = lazy(() =>
  import('@/pages/kansalaiselle/KansalaisellePage').then((m) => ({ default: m.KansalaisellePage })),
)

// WP-F5: /tarkoitus reads the full vault-index.json for its live spec-type
// counts, and /spec + /spec/:id render any of the 561 specs from it — all
// three lazy for the same ~5 MB reason as WalkPage above. /graph only needs
// the much smaller graph.json, but pulls in cytoscape.js, so it stays lazy too.
const TarkoitusPage = lazy(() => import('@/pages/tarkoitus/TarkoitusPage').then((m) => ({ default: m.TarkoitusPage })))
const SpecIndexPage = lazy(() => import('@/pages/spec/SpecIndexPage').then((m) => ({ default: m.SpecIndexPage })))
const SpecViewerPage = lazy(() => import('@/pages/spec/SpecViewerPage').then((m) => ({ default: m.SpecViewerPage })))
const GraphPage = lazy(() => import('@/pages/graph/GraphPage').then((m) => ({ default: m.GraphPage })))

// WP-F6: /tyontekijalle and /lapileikkaus both reuse the walk engine (via
// useDecision) exactly like WalkPage above — same ~5 MB vault-index/engine
// weight, same reason to lazy-load and keep off the front page.
const TyontekijallePage = lazy(() =>
  import('@/pages/tyontekijalle/TyontekijallePage').then((m) => ({ default: m.TyontekijallePage })),
)
const LapileikkausPage = lazy(() =>
  import('@/pages/lapileikkaus/LapileikkausPage').then((m) => ({ default: m.LapileikkausPage })),
)

// WP-S1: /synteettinen-suomi carries the sql.js-httpvfs wasm + worker and queries
// a 39 MB slim SQLite over HTTP range requests — by far the heaviest route. Lazy
// for the same front-page-never-pays reason, and doubly so: the DB + wasm load
// only when this route mounts, never on any other page.
const SynteettinenSuomiPage = lazy(() =>
  import('@/pages/synteettinen-suomi/SynteettinenSuomiPage').then((m) => ({ default: m.SynteettinenSuomiPage })),
)

// WP-V7: /arkkivaltio carries the hand-authored SeamsMap SVG and one MermaidCanvas
// (the same large mermaid dependency as /data-model) — lazy for the same
// front-page-never-pays reason.
const ArkkivaltioPage = lazy(() =>
  import('@/pages/arkkivaltio/ArkkivaltioPage').then((m) => ({ default: m.ArkkivaltioPage })),
)

// Route table = DESIGN-BRIEF §2. All twelve routes exist from day one; the ones
// later WPs build render a consistent under-construction state for now. Real
// pages in WP-E1: the front page (/) and the style-sample subpage
// (/tarkoitus/tarina).
export default function App() {
  return (
    <>
      <ScrollManager />
      <Routes>
        <Route element={<PageShell />}>
          <Route index element={<FrontPage />} />

          {/* Style-sample subpage — more specific than /tarkoitus, listed first. */}
          <Route path="tarkoitus/tarina" element={<TarinaPage />} />

          {/* Placeholder loop for not-yet-shipped areas. Every current AREAS
              entry has a real route below, so the loop is empty and stands
              ready only for any future area. */}
          {AREAS.filter(
            (area) =>
              area.slug !== '/data-model' &&
              area.slug !== '/kansalaiselle' &&
              area.slug !== '/tarkoitus' &&
              area.slug !== '/tyontekijalle' &&
              area.slug !== '/lapileikkaus' &&
              area.slug !== '/arkkivaltio' &&
              area.slug !== '/synteettinen-suomi',
          ).map((area) => (
            <Route
              key={area.slug}
              path={area.slug}
              element={<UnderConstruction title={t(area.labelKey)} buildWp={area.buildWp} />}
            />
          ))}

          <Route
            path="data-model"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('dataModel.pageLoading')}</div>}>
                <DataModelPage />
              </Suspense>
            }
          />

          <Route
            path="kansalaiselle"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('catalog.pageLoading')}</div>}>
                <KansalaisellePage />
              </Suspense>
            }
          />

          <Route
            path="tarkoitus"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('tarkoitus.pageLoading')}</div>}>
                <TarkoitusPage />
              </Suspense>
            }
          />

          {/* Utility / parameterized routes (WP-F5). /spec (exact) is the folder-tree
              index; /spec/:id the universal viewer — both share one Suspense
              fallback text since they're both reached via the same "Konepellin
              alle" affordances. */}
          <Route
            path="spec"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('spec.page.loading')}</div>}>
                <SpecIndexPage />
              </Suspense>
            }
          />
          <Route
            path="spec/:id"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('spec.page.loading')}</div>}>
                <SpecViewerPage />
              </Suspense>
            }
          />
          <Route
            path="graph"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('graph.page.loading')}</div>}>
                <GraphPage />
              </Suspense>
            }
          />
          <Route
            path="tyontekijalle"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('tyontekijalle.pageLoading')}</div>}>
                <TyontekijallePage />
              </Suspense>
            }
          />
          <Route
            path="lapileikkaus"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('lapileikkaus.pageLoading')}</div>}>
                <LapileikkausPage />
              </Suspense>
            }
          />
          <Route
            path="synteettinen-suomi"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('synteettinenSuomi.pageLoading')}</div>}>
                <SynteettinenSuomiPage />
              </Suspense>
            }
          />
          <Route
            path="arkkivaltio"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('arkkivaltio.pageLoading')}</div>}>
                <ArkkivaltioPage />
              </Suspense>
            }
          />
          <Route
            path="walk/:benefit/:scenario"
            element={
              <Suspense fallback={<div className="mx-auto min-h-screen max-w-shell px-4 py-16 text-muted">{t('walk.loading')}</div>}>
                <WalkPage />
              </Suspense>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  )
}
